import React, { useState } from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio as MuiRadio, // Alias MuiRadio to avoid naming conflict
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  RadioGroupProps,
  FormLabelProps,
  FormControlLabelProps,
  SxProps,
} from '@mui/material';
import { useController, useFormContext, useWatch } from 'react-hook-form';

// Define the expected props, similar to the base Radio component but adding ours
interface ConfirmationRadioGroupProps {
  name: string;
  control: any;
  label: string;
  options: { label: string; value: any }[];
  listFieldName: string;
  confirmationText?: string;
  defaultValue?: any;
  labelProps?: FormLabelProps;
  formControlLabelProps?: FormControlLabelProps;
  sx?: SxProps; // For the RadioGroup
}

const ConfirmationRadioGroup: React.FC<ConfirmationRadioGroupProps> = ({ 
  name,
  control,
  label,
  options,
  listFieldName,
  confirmationText,
  defaultValue,
  labelProps,
  formControlLabelProps,
  sx,
  ...rest // Collect any other RadioGroupProps
}) => {
  const { control: contextControl, formState } = useFormContext(); // Use context if needed
  const actualControl = control || contextControl;

  // Get field state using useController
  const { 
    field: { onChange, value, ref },
   } = useController({
    name,
    control: actualControl,
    defaultValue: defaultValue ?? null, 
  });

  // Watch the dependent list field
  const listData = useWatch({
    control: actualControl,
    name: listFieldName || '',
    disabled: !listFieldName,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [pendingValue, setPendingValue] = useState<any>(null);

  // The actual handler attached to the MUI RadioGroup
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    // Convert string 'true'/'false' back to boolean if necessary
    const newValue = rawValue === 'true' ? true : (rawValue === 'false' ? false : rawValue);

    // Check if changing from Yes (true) to No (false) and if the list has data
    if (value === true && newValue === false && listFieldName && Array.isArray(listData) && listData.length > 0) {
      setPendingValue(newValue);
      setOpenDialog(true);
    } else {
      // Otherwise, call the react-hook-form onChange immediately
      onChange(newValue);
    }
  };

  const handleConfirm = () => {
    onChange(pendingValue);
    setOpenDialog(false);
    setPendingValue(null);
  };

  const handleCancel = () => {
    setOpenDialog(false);
    setPendingValue(null);
  };

  const finalConfirmationText = confirmationText || `Are you sure you want to remove all items you have listed? This action will clear the list upon saving.`;
  const error = formState.errors[name];

  return (
    <>
      <FormControl component="fieldset" error={!!error} sx={{ mb: 1, width: '100%' }}>
        {label && <FormLabel component="legend" {...labelProps}>{label}</FormLabel>}
        <RadioGroup
          {...rest} // Spread any additional RadioGroupProps
          row // Defaulting to row layout
          aria-label={name}
          name={name}
          value={value !== null && value !== undefined ? String(value) : ''} // Ensure value is string for RadioGroup
          onChange={handleRadioChange} // Use our custom handler
          sx={sx}
          ref={ref} // Pass the ref from useController
        >
          {options.map((option) => (
            <FormControlLabel
              key={String(option.value)} // Use string value for key
              value={String(option.value)} // Ensure value is string for FormControlLabel
              control={<MuiRadio />} // Use the imported MuiRadio
              label={option.label}
              {...formControlLabelProps}
            />
          ))}
        </RadioGroup>
        {error && <FormLabel error sx={{ fontSize: '0.75rem', mt: 0.5 }}>{error?.message || 'Invalid input'}</FormLabel>} 
      </FormControl>

      {/* Dialog remains the same */}
      <Dialog
        open={openDialog}
        onClose={handleCancel}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
				sx={{
					"& .MuiDialog-paper": {
						padding: 1,
					}
				}}
      >
        <DialogContent>
          <DialogContentText id="confirmation-dialog-description">
            {finalConfirmationText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmationRadioGroup; 